import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';

function ModeChip({ meta }) {
  if (!meta?.mode) return null;
  const isRag = meta.mode === 'rag';
  const words = meta.transcriptChars
    ? Math.round(meta.transcriptChars / 6)
    : null;
  const label = isRag
    ? `RAG · ${meta.contextChunks ?? '?'} Ausschnitte`
    : 'Vollständiges Transkript';
  const tooltip = isRag
    ? `Das Transkript ist für das Kontextfenster (${meta.numCtx ?? '?'} Tokens) zu lang. Es werden automatisch die ${meta.contextChunks ?? ''} thematisch passendsten Ausschnitte verwendet.`
    : `Das komplette Transkript${words ? ` (~${words} Wörter)` : ''} wurde an das Modell übergeben.`;
  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        size="small"
        variant="outlined"
        color={isRag ? 'warning' : 'success'}
        icon={isRag ? <CenterFocusStrongIcon /> : <ArticleIcon />}
        label={label}
      />
    </Tooltip>
  );
}

export default function OneClickResultDialog({
  open,
  onClose,
  title,
  icon,
  text,
  isStreaming,
  error,
  meta,
  cachedAt,
  filename,
  onAbort,
  onRerun,
}) {
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch {
      /* ignore */
    }
  };

  const downloadText = () => {
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.href = url;
    element.download = filename || 'one-click-ergebnis.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ fontSize: 22 }}>{icon || '✨'}</span>
        <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
        {isStreaming && <CircularProgress size={18} />}
        <IconButton onClick={onClose} size="small" aria-label="Schließen">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {meta?.mode && (
          <Box mb={1.5}>
            <ModeChip meta={meta} />
          </Box>
        )}
        {cachedAt && !isStreaming && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Zwischengespeichertes Ergebnis vom {new Date(cachedAt).toLocaleString('de-DE')}
          </Typography>
        )}
        {error && (
          <Typography color="error" variant="body2" mb={1}>{error}</Typography>
        )}
        <Box
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'inherit',
            fontSize: 14,
            m: 0,
            minHeight: 120,
          }}
        >
          {text || (isStreaming ? 'Wird erstellt…' : 'Keine Ausgabe.')}
        </Box>
      </DialogContent>
      <DialogActions>
        {isStreaming ? (
          <Button startIcon={<StopIcon />} color="warning" onClick={onAbort}>
            Abbrechen
          </Button>
        ) : (
          <>
            {onRerun && (
              <Button startIcon={<ReplayIcon />} onClick={onRerun}>
                Neu ausführen
              </Button>
            )}
            <Button startIcon={<DownloadIcon />} onClick={downloadText} disabled={!text}>
              Herunterladen
            </Button>
            <Tooltip title="In Zwischenablage kopieren">
              <span>
                <Button
                  startIcon={<ContentCopyIcon />}
                  onClick={copyToClipboard}
                  disabled={!text}
                >
                  Kopieren
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
